import {
  accountFromMnemonic,
  createAccount,
  isValidMnemonic,
  signChallenge,
  summarizeReceipts,
  verifyReceipt,
  type Account,
  type Receipt,
} from "@hoa/crypto";
import { storage } from "./storage.js";

/**
 * Compte éphémère local (ch. 7). La graine ne quitte jamais l'appareil ;
 * la désinstallation, ou l'effacement des données du site, supprime tout.
 */

const K_MNEMONIC = "mnemonic";
const K_PSEUDO = "pseudo";
const K_SECTOR = "secteur";
/** L'e-mail associé au compte : c'est lui qui fait un « vrai » compte (lot 2). */
const K_EMAIL = "email";
const K_RECEIPTS = "recus";
const K_CONSENT = "consentement";

export interface LocalAccount {
  account: Account;
  pseudo: string;
  sectorId: string | null;
  receipts: Receipt[];
  /**
   * Horodatage du consentement donné à la création : âge minimum, position,
   * son et mise en relation. Il évite de redemander la même chose à chaque
   * entrée en tribu. Un compte restauré depuis douze mots ne l'a pas : il est
   * alors redemandé une fois.
   */
  consentAt: number | null;
  /**
   * L'e-mail du compte. Sans lui, l'appareil n'a qu'une clé technique : il
   * peut rejoindre des tribus et changer de pseudo, pas en ouvrir une ni
   * débloquer les autres personnalisations.
   */
  email: string | null;
}

export async function hasAccount(): Promise<boolean> {
  return (await storage.get(K_MNEMONIC)) !== null;
}

export async function loadAccount(): Promise<LocalAccount | null> {
  const mnemonic = await storage.get(K_MNEMONIC);
  if (!mnemonic || !isValidMnemonic(mnemonic)) return null;
  const consent = await storage.get(K_CONSENT);
  return {
    account: accountFromMnemonic(mnemonic),
    pseudo: (await storage.get(K_PSEUDO)) ?? "",
    sectorId: await storage.get(K_SECTOR),
    receipts: JSON.parse((await storage.get(K_RECEIPTS)) ?? "[]") as Receipt[],
    consentAt: consent ? Number(consent) : null,
    email: (await storage.get(K_EMAIL)) ?? null,
  };
}

/**
 * Création d'un compte. L'utilisateur ne donne qu'un pseudo : la graine, les
 * douze mots et la clé sont fabriqués silencieusement sur l'appareil. La
 * phrase de restauration ne s'affiche que plus tard, depuis le profil, quand
 * la personne demande à exporter son compte.
 */
export async function createLocalAccount(pseudo = "", consent = false): Promise<LocalAccount> {
  const account = createAccount();
  await storage.set(K_MNEMONIC, account.mnemonic);
  if (pseudo) await storage.set(K_PSEUDO, pseudo);
  const consentAt = consent ? Date.now() : null;
  if (consentAt) await storage.set(K_CONSENT, String(consentAt));
  await storage.requestPersistence();
  return { account, pseudo, sectorId: null, receipts: [], consentAt, email: null };
}

/** Enregistre le consentement, pour un compte restauré qui n'en a pas. */
export async function enregistrerConsentement(): Promise<number> {
  const at = Date.now();
  await storage.set(K_CONSENT, String(at));
  return at;
}

/** Retient (ou oublie) l'e-mail du compte auquel ce téléphone est relié. */
export async function enregistrerEmail(email: string | null): Promise<void> {
  if (email) await storage.set(K_EMAIL, email);
  else await storage.remove(K_EMAIL);
}

export async function setPseudo(pseudo: string): Promise<void> {
  await storage.set(K_PSEUDO, pseudo);
}

export async function restoreLocalAccount(mnemonic: string): Promise<LocalAccount> {
  const account = accountFromMnemonic(mnemonic);
  await storage.set(K_MNEMONIC, account.mnemonic);
  await storage.requestPersistence();
  return { account, pseudo: "", sectorId: null, receipts: [], consentAt: null, email: (await storage.get(K_EMAIL)) ?? null };
}

export async function savePreferences(pseudo: string, sectorId: string | null): Promise<void> {
  await storage.set(K_PSEUDO, pseudo);
  if (sectorId) await storage.set(K_SECTOR, sectorId);
}

export async function addReceipt(receipt: Receipt): Promise<void> {
  const list = JSON.parse((await storage.get(K_RECEIPTS)) ?? "[]") as Receipt[];
  if (!verifyReceipt(receipt)) return;
  if (list.some((r) => r.session === receipt.session)) return;
  list.push(receipt);
  await storage.set(K_RECEIPTS, JSON.stringify(list));
}

export async function statistics(pub: string): Promise<{ sessions: number; met: number; almost: number }> {
  const list = JSON.parse((await storage.get(K_RECEIPTS)) ?? "[]") as Receipt[];
  const s = summarizeReceipts(list, { pub });
  return { sessions: s.sessions, met: s.met, almost: s.almost };
}

/** Export de l'historique — proposition PO-03, à confirmer. */
export async function exportReceipts(): Promise<string> {
  return (await storage.get(K_RECEIPTS)) ?? "[]";
}

/** Propose le téléchargement de l'historique signé, sur l'appareil. */
export async function telechargerHistorique(pub: string): Promise<void> {
  const contenu = JSON.stringify({ v: 1, pub, receipts: JSON.parse(await exportReceipts()) }, null, 2);
  const url = URL.createObjectURL(new Blob([contenu], { type: "application/json" }));
  const a = document.createElement("a");
  a.href = url;
  a.download = `hoa-historique-${pub.slice(0, 8)}.json`;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 2000);
}

export async function forgetEverything(): Promise<void> {
  await storage.clear();
}

export { signChallenge };
export type { Account, Receipt };
